import { Button } from 'antd';
import React, { useRef, useEffect, useState } from 'react';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import dayjs from 'dayjs';
import _ from 'lodash';
import { Trans, useTranslation } from 'react-i18next';

// IMG
import closeIcon from '@icons/Icon_Close_10.svg';
import chatMinIcon from '@icons/Icon_chat_close.svg';
import chatService from '@app/service/chatService';
import Icon_plus from '@icons/Icon_plus.svg';
import Icon_photo from '@icons/Icon_photo_attach.svg';
import Icon_file from '@icons/Icon_file_attach.svg';
import Icon_down from '@icons/Icon_down.svg';

import userStore from '@app/stores/userStore';
import chatStore from '@app/stores/chatStore';
import ChattingImagesViewer from './ChattingImagesViewer';
import fileService from '@app/service/fileService';
import { useToast } from '@app/context/ToastContext';
import { downloadFile } from '@app/utils/file';
import ChatAuthorLabel from './ChatAuthorLabel';
import ChatInquiryDetailButton from './ChatInquiryDetailButton';
import { isLooseEqual } from '@app/utils/equality';

dayjs.extend(localizedFormat);

export default function ChatContent({
  chattingRoom,
  messages,
  open,
  setOpen,
  dragHandleProps = {},
  headerActionLabel,
  headerCloseLabel,
  onRemove,
}) {
  const { t } = useTranslation();
  const textareaRef = useRef();
  const scrollRef = useRef();
  const imagesInputRef = useRef();
  const fileInputRef = useRef();
  const beforeLastSeq = useRef(null);
  // 메시지 변경 때만 스크롤 effect를 실행하면서 현재 채팅방과 스크롤 함수를 참조합니다.
  const chattingRoomRef = useRef(chattingRoom);
  const scrollToBottomRef = useRef(null);
  chattingRoomRef.current = chattingRoom;

  const { showToast } = useToast();
  const {
  myInfo
} = userStore();
  useState([]); // 선택 된 파일
  const [textValue, setTextValue] = useState('');
  const [viewFileUpload, setViewFileUpload] = useState(false);
  const { nextPage, removeChattingRoom } = chatStore();
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const isDraggableHeader = Object.keys(dragHandleProps).length > 0;

  const handleResizeHeight = () => {
    textareaRef.current.style.height = 'auto'; //height 초기화
    textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
  };

  useEffect(() => {
    textareaRef.current.focus();
  }, []);

  useEffect(() => {
    let loadedImagesCount = 0;
    let totalImagesCount = 0;
    chattingRoomRef.current?.messages
      .filter((msg, i) => isLooseEqual(msg.mimeType, 'IMG'))
      .forEach((msg, i) => {
        msg.chatFiles.forEach((file, f) => {
          totalImagesCount = totalImagesCount + 1;
        });
      });

    // 모든 이미지가 로드될 때까지 기다림
    const checkAllImagesLoaded = () => {
      loadedImagesCount++;
      if (loadedImagesCount === totalImagesCount) {
        setImagesLoaded(true);
      }
    };

    chattingRoomRef.current?.messages
      .filter((msg, i) => isLooseEqual(msg.mimeType, 'IMG'))
      .forEach((msg, i) => {
        msg.chatFiles.forEach((file, f) => {
          const img = new Image();
          img.onload = checkAllImagesLoaded;
          img.src = file.fileUrl;
        });
      });

    scrollToBottomRef.current();
  }, [messages]);

  useEffect(() => {
    if (imagesLoaded) {
      scrollToBottomRef.current();
      setImagesLoaded(false);
    }
  }, [imagesLoaded]);

  const scrollToBottom = () => {
    if (beforeLastSeq.current) {
      scrollToElement(`${chattingRoom.id}-message-${beforeLastSeq.current}`);
      beforeLastSeq.current = null;
      return;
    }
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  };
  scrollToBottomRef.current = scrollToBottom;

  const scrollToElement = (id) => {
    const element = document.getElementById(id);
    if (element && scrollRef.current) {
      scrollRef.current.scrollTo({
        top: element.offsetTop,
        behavior: 'auto',
      });
    }
  };

  const handleScroll = _.debounce(() => {
    const container = scrollRef.current;
    if (!container) return;
    if (isLooseEqual(container.scrollTop, 0)) {
      beforeLastSeq.current = messages[0].seq;
      nextPage(chattingRoom.id);
    }
  }, 0);

  const handleKeyDown = (event) => {
    if (event.nativeEvent.isComposing) {
      // isComposing 이 true 이면
      return; // 조합 중이므로 동작을 막는다.
    }
    if (event.ctrlKey && event.key === 'Enter') {
      // 컨트롤 + 엔터 키 입력 감지됨
      // 또는 원하는 동작 수행
      setTextValue(`${textValue}\n`);

      return;
    }

    if (event.key === 'Enter') {
      // 엔터 키 입력 감지됨
      // 또는 원하는 동작 수행
      event.preventDefault();
      sendMessage();
    }
  };

  function sendMessage() {
    if (textValue == null || textValue.replaceAll(' ', '').length === 0) {
      return;
    }
    chatService
      .sendMessage(chattingRoom.id, {
        message: textValue,
        mimeType: 'TEXT',
      })
      .then((res) => {
        setTextValue('');
        textareaRef.current.style.height = 'auto';
      });
  }

  return (
    <div className={'w-100 h-100 flex-column align-stretch'} style={{}}>
      <div
        className={'flex-row align-center gap-7 pl-16 pr-65'}
        style={{ height: '51px' }}
      >
        <div
          {...dragHandleProps}
          className="flex-row align-center gap-7"
          style={{
            minWidth: 0,
            flex: 1,
            height: '100%',
            cursor: isDraggableHeader ? 'move' : 'default',
            touchAction: isDraggableHeader ? 'none' : 'auto',
          }}
        >
          <span
            style={{
              fontSize: '13px',
              fontWeight: '600',
              lineHeight: '20px',
              color: '#000',
              whiteSpace: 'nowrap',
              overflow: 'hidden' /* 넘치는 텍스트를 숨김 */,
              textOverflow: 'ellipsis',
              cursor: 'inherit',
            }}
          >
            {chattingRoom.reservation?.travel?.name}
          </span>
          <span style={{ cursor: 'inherit' }}>
            {chattingRoom.reservation?.reservationNumber
              ? chattingRoom.reservation?.reservationNumber
              : ''}
          </span>
        </div>
        <ChatInquiryDetailButton reservationId={chattingRoom.id} />
        <div
          className="flex-row align-center"
          style={{ position: 'absolute', right: 3, top: 3 }}
        >
          <Button
            type="text"
            aria-label={headerActionLabel}
            icon={
              <img
                alt={headerActionLabel ? '' : t('common:imageAlt.minimize')}
                src={chatMinIcon}
              />
            }
            style={{
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '30px',
              height: '30px',
            }}
            onPointerDown={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
            onClick={() => {
              setOpen(!open);
            }}
          />
          <Button
            type="text"
            aria-label={headerCloseLabel}
            icon={
              <img
                alt={headerCloseLabel ? '' : t('common:imageAlt.close')}
                src={closeIcon}
              />
            }
            style={{
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '30px',
              height: '30px',
            }}
            onPointerDown={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
            onClick={() => {
              if (onRemove) onRemove();
              else removeChattingRoom(chattingRoom.id);
            }}
          />
        </div>
      </div>
      <div
        className={'flex-column pb-10'}
        style={{ backgroundColor: '#F7F7F7', flex: 1, overflowY: 'auto' }}
        ref={scrollRef}
        onScroll={handleScroll}
      >
        {messages.map((msg, i) => {
          const isRead = chattingRoom.checkSeq >= msg.seq;
          const title = `${chattingRoom.reservation?.travel?.name} ${
            chattingRoom.reservation?.reservationNumber
              ? chattingRoom.reservation?.reservationNumber
              : ''
          }`;
          if (i === 0) {
            return (
              <ChatItem
                title={title}
                roomId={chattingRoom.id}
                message={msg}
                key={msg.seq}
                isRead={isRead}
              />
            );
          } else {
            const before = messages[i - 1];
            const isHideSender = isLooseEqual(before.fromUserNo, msg.fromUserNo);
            const isHideDate =
              dayjs(before.created)?.format('YYYYMMDD') ===
              dayjs(msg.created)?.format('YYYYMMDD');
            if (i === messages.length - 1) {
              return (
                <ChatItem
                  title={title}
                  roomId={chattingRoom.id}
                  key={msg.seq}
                  message={msg}
                  isHideSender={!isHideDate ? false : isHideSender}
                  isHideDate={isHideDate}
                  isRead={isRead}
                />
              );
            } else {
              const after = messages[i + 1];
              const isHideTime =
                isLooseEqual(before.fromUserNo, msg.fromUserNo) &&
                dayjs(after.created)?.format('HH:mm') ===
                  dayjs(msg.created)?.format('HH:mm');
              return (
                <ChatItem
                  title={title}
                  roomId={chattingRoom.id}
                  key={msg.seq}
                  message={msg}
                  isHideSender={isHideDate && isHideSender}
                  isHideDate={isHideDate}
                  isHideTime={isHideTime}
                  isRead={isRead}
                />
              );
            }
          }
        })}
      </div>
      <div className={'flex-column'}>
        <textarea
          value={textValue}
          rows={2}
          className="mt-10 ml-10 mr-10"
          style={{ maxHeight: '66px', resize: 'none', border: 'none' }}
          ref={textareaRef}
          onChange={(e) => {
            const value = e.target.value;
            setTextValue(value);
            handleResizeHeight();
            e.preventDefault();
          }}
          onKeyDown={handleKeyDown}
        />
        <div style={{ borderTop: '1px solid #E6E6E6' }} />
        <div
          className={'flex-row justify-end'}
          style={{ padding: '7px 7px 9px 7px' }}
        >
          {/* <Upload
            beforeUpload={() => false}
            multiple={true}
            fileList={fileList}
            maxCount={9}
            accept={'image/*'}
            showUploadList={false}
            onChange={(value) => {
              const fileList = value.fileList;
              if (fileList.length > 20) {
                showToast(
                  `한 번에 전송할 수 있는 이미지 개수는 최대 20개입니다.`,
                );
                return;
              }
              console.log(value);
              console.log(value.file.status);
              console.log(fileList);
              setFileList(fileList);
              // fileService.uploadFile(fileList, {
              //   businessId: myInfo.travel.travelId,
              //   contentTypeCode: 'CHAT_IMG',
              //   thumbnail: true,
              // });
            }}
          > */}
          <Button
            style={{ padding: 0 }}
            type="text"
            icon={<img alt={t('common:imageAlt.addFile')} src={Icon_plus} />}
            onClick={() => {
              setViewFileUpload(!viewFileUpload);
            }}
          />
          <input
            ref={imagesInputRef}
            type="file"
            id="upload"
            accept={'image/jpeg, image/png'}
            hidden
            multiple
            onChange={(e) => {
              const selectedFiles = Array.from(e.target.files);
              if (selectedFiles.length === 0) {
                return;
              }
              if (selectedFiles.length > 20) {
                showToast(
                  <Trans
                    i18nKey="inquiry:한번에전송할수있는이미지개수는최대."
                    values={{ length: 20 }}
                    defaults={
                      '한 번에 전송할 수 있는 이미지 개수는 최대 {{length}}개입니다.'
                    }
                  />,
                );
                return;
              }
              fileService
                .uploadFile(selectedFiles, {
                  businessId: myInfo.hotel.hotelId,
                  contentTypeCode: 'CHAT_IMG',
                  thumbnail: true,
                })
                .then((res) => {
                  const uploadFiles = res.data.data.map((file, i) => {
                    file.fileId = file.id;
                    file.fileName = file.originalFileName;
                    file.ord = i;

                    delete file.id;
                    delete file.mime;
                    delete file.originalFileName;
                    delete file.thumbnailUrl;

                    return file;
                  });
                  chatService
                    .sendMessage(chattingRoom.id, {
                      chatFiles: uploadFiles,
                      mimeType: 'IMG',
                    })
                    .then((res) => {
                      setViewFileUpload(false);
                    })
                    .catch((e) => console.log('e', e));
                })
                .catch((e) => console.log('e', e));
            }}
          />
          <input
            ref={fileInputRef}
            type="file"
            id="upload"
            accept={''}
            hidden
            multiple={false}
            onChange={(e) => {
              const selectedFiles = Array.from(e.target.files);
              if (selectedFiles.length === 0) {
                return;
              }
              if (selectedFiles.length > 1) {
                showToast(
                  <Trans
                    i18nKey="inquiry:한번에전송할수있는이미지개수는최대."
                    values={{ length: 1 }}
                    defaults={
                      '한 번에 전송할 수 있는 이미지 개수는 최대 {{length}}개입니다.'
                    }
                  />,
                );
                return;
              }
              fileService
                .uploadFile(selectedFiles, {
                  businessId: myInfo.hotel.hotelId,
                  contentTypeCode: 'CHAT_FILE',
                  thumbnail: false,
                })
                .then((res) => {
                  const uploadFiles = res.data.data.map((file, i) => {
                    file.fileId = file.id;
                    file.fileName = file.originalFileName;
                    file.ord = i;

                    delete file.id;
                    delete file.mime;
                    delete file.originalFileName;

                    return file;
                  });
                  chatService
                    .sendMessage(chattingRoom.id, {
                      chatFiles: uploadFiles,
                      mimeType: 'FILE',
                    })
                    .then((res) => {
                      setViewFileUpload(false);
                    })
                    .catch((e) => console.log('e', e));
                })
                .catch((e) => console.log('e', e));
            }}
          />
          {/* </Upload> */}
          <Button
            type="primary"
            disabled={false}
            onClick={() => {
              sendMessage();
            }}
            style={{
              width: '46px',
              height: '29px',
              fontWeight: 550,
              padding: 0,
            }}
          >
            {t('전송')}
          </Button>
        </div>
        {viewFileUpload && (
          <div
            className={'flex-row align-center'}
            style={{
              height: '74px',
              width: '100%',
              padding: '0 10px',
              gap: '10px',
              cursor: 'pointer',
              borderTop: '1px solid #E6E6E6',
            }}
          >
            <div
              className={'flex-row align-center gap-12'}
              style={{ padding: '0 10px' }}
              onClick={() => {
                imagesInputRef.current.click();
              }}
            >
              <img alt="" src={Icon_photo} />
              <span style={{ fontSize: '13px', color: '#000' }}>
                {t('사진 첨부')}
              </span>
            </div>
            <div
              className={'flex-row align-center gap-12'}
              style={{ padding: '0 10px' }}
              onClick={() => {
                fileInputRef.current.click();
              }}
            >
              <img alt="" src={Icon_file} />
              <span style={{ fontSize: '13px', color: '#000' }}>
                {t('파일 첨부')}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ChatItem({
  title,
  roomId,
  message,
  isHideSender = false,
  isHideDate = false,
  isHideTime = false,
  isRead = false,
}) {
  const { t } = useTranslation();
  const [isMine, setIsMine] = useState(true);
  const { myInfo } = userStore();

  useEffect(() => {
    setIsMine(
      isLooseEqual(message.fromBizType, 'HOTEL') &&
        isLooseEqual(myInfo?.hotel.hotelId, message.fromBizId),
    );
  }, [myInfo, message]);

  return (
    <>
      {!isHideDate && (
        <div className={`flex-row justify-center align-center pt-20`}>
          <span
            style={{
              fontSize: '12px',
              fontWeight: '400',
              lineHeight: '12px',
              color: '#767676',
            }}
          >
            {dayjs(message.created).format('LL dddd')}
          </span>
        </div>
      )}
      <div
        id={`${roomId}-message-${message.seq}`}
        className={`flex-column gap-8 pl-14 pr-14 ${
          isHideSender && isHideDate ? 'pt-6' : 'pt-20'
        } ${isMine ? 'align-end' : 'align-start'}`}
      >
        {!isHideSender && (
          <ChatAuthorLabel
            name={message.fromName}
            position={message.fromPosition}
            style={{
              fontSize: '12px',
              fontWeight: '400',
              lineHeight: '12px',
            }}
          />
        )}
        <div className={`${isMine ? 'flex-row-reverse' : 'flex-row'} gap-6`}>
          {isLooseEqual(message?.mimeType, 'IMG') && (
            // {true && (
            <ChattingImagesViewer
              id={roomId}
              seq={message.seq}
              detailTitle={title}
              images={message?.chatFiles?.map((file, i) => {
                return file.fileUrl;
              })}
            />
          )}
          {isLooseEqual(message?.mimeType, 'FILE') && (
            // {true && (
            <div
              className={'flex-column gap-4'}
              style={{
                position: 'relative',
                backgroundColor: isMine ? '#E1F1FF' : '#FFF',
                borderRadius: '5px',
                padding: '8px 12px',
              }}
            >
              <span
                style={{
                  color: '#000000',
                  fontSize: '13px',
                  lineHeight: '20px',
                  display: '-webkit-box' /* 웹킷 브라우저에서 사용하는 속성 */,
                  WebkitLineClamp: '2' /* 최대 두 줄까지 표시 */,
                  WebkitBoxOrient: 'vertical' /* 수직 방향으로 텍스트 배치 */,
                  overflow: 'hidden' /* 초과하는 텍스트 숨김 */,
                }}
              >
                {message.chatFiles[0].fileName}
              </span>
              <span
                style={{
                  color: '#767676',
                  fontSize: '12px',
                  lineHeight: '18px',
                  marginRight: '32px',
                }}
              >
                {t('유효기간')}: ~{t('전송일로 부터 15일')}
                <br />
                {t('용량')}:{(message.chatFiles[0].fileSize / 1024).toFixed(1)}
                kb
              </span>
              <Button
                style={{
                  padding: 0,
                  position: 'absolute',
                  right: 4,
                  bottom: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                type="text"
                icon={<img alt={t('common:imageAlt.download')} src={Icon_down} />}
                onClick={() => {
                  downloadFile(
                    message.chatFiles[0].fileUrl,
                    message.chatFiles[0].fileName.split('.')[0],
                  );
                }}
              />
            </div>
          )}
          {isLooseEqual(message?.mimeType, 'TEXT') && (
            <div
              style={{
                backgroundColor: isMine ? '#E1F1FF' : '#FFF',
                borderRadius: '5px',
                padding: '8px 12px',
              }}
            >
              <span
                style={{
                  fontSize: '13px',
                  fontWeight: '400',
                  lineHeight: '20px',
                  whiteSpace: 'pre-line',
                }}
              >
                {message?.message}
              </span>
            </div>
          )}

          <div
            className={`flex-column justify-end ${
              isMine ? 'align-end' : 'align-start'
            } gap-5`}
            style={{
              // width: '40px',
              minWidth: '40px',
            }}
          >
            {/* <span
              style={{
                fontSize: '10px',
                fontWeight: '400',
                lineHeight: '10px',
                color: '#999999',
              }}
            >
              {message.seq}
            </span> */}
            {!isRead && isMine && (
              <span
                style={{
                  fontSize: '10px',
                  fontWeight: '400',
                  lineHeight: '10px',
                  color: '#999999',
                }}
              >
                {t('안읽음')}
              </span>
            )}
            {!isHideTime && (
              <>
                <span
                  style={{
                    fontSize: '10px',
                    fontWeight: '400',
                    lineHeight: '10px',
                    color: '#999999',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {dayjs(message.created).format('A hh:mm')}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
